import type React from 'react';

declare const __BUILD_HASH__: string;

/**
 * Persist a React render-time crash to Firestore so we can post-mortem
 * looping/render-error bugs across sessions. Best-effort: any failure
 * is swallowed because the boundary is already in the failure path —
 * we must never throw out of here.
 *
 * The componentStack is the most useful artefact: even in production
 * minified builds it names every ancestor of the looping component
 * provided each memo'd / wrapped component has a displayName set.
 */
export async function persistCrashTelemetry(
  error: Error,
  errorInfo: React.ErrorInfo,
): Promise<void> {
  try {
    const { auth, db } = await import('../firebase');
    const user = auth.currentUser;
    if (!user) return;
    const { doc, setDoc } = await import('firebase/firestore');
    const id = `crash-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const buildHash = (() => {
      try {
        return typeof __BUILD_HASH__ === 'string' ? __BUILD_HASH__ : 'unknown';
      } catch {
        return 'unknown';
      }
    })();
    await setDoc(doc(db, `users/${user.uid}/crashes`, id), {
      id,
      createdAt: new Date().toISOString(),
      message: String(error?.message || error || '').slice(0, 1000),
      name: String(error?.name || 'Error').slice(0, 100),
      stack: (error?.stack || '').slice(0, 4000),
      componentStack: (errorInfo?.componentStack || '').slice(0, 8000),
      url: typeof window !== 'undefined' ? window.location.href : 'ssr',
      userAgent:
        typeof navigator !== 'undefined' ? navigator.userAgent.slice(0, 300) : 'unknown',
      buildHash,
    });
  } catch {
    /* swallow — best-effort */
  }
}
